"use client";

import posthogRaw, { type PostHog } from "posthog-js";
import { PostHogProvider as PostHogProviderRaw } from "posthog-js/react";
import type { ReactNode } from "react";

// PostHog configuration for admin app

// Get environment variables specific to the admin app
const POSTHOG_KEY = process.env.NEXT_PUBLIC_NUDJ_LEGACY_POSTHOG_KEY || "";
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_NUDJ_LEGACY_POSTHOG_HOST ||
  "https://eu.i.posthog.com";

// Get the current domain's api_host for PostHog
const getApiHost = () => {
  if (typeof window === "undefined") {
    return "/ingest"; // Fallback for SSR
  }

  // Use the current domain for PostHog ingest, like the preview functionality
  return `${window.location.protocol}//${window.location.host}/ingest`;
};

// Try with explicit initialization to avoid any issues
let analyticsInstance: PostHog | undefined;
try {
  analyticsInstance = posthogRaw.init(POSTHOG_KEY, {
    api_host: getApiHost(),
    ui_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    debug: false, // Disable debug logs
    // Remove disable flags to enable full functionality
    // disable_session_recording: true,
    // advanced_disable_toolbar_metrics: true,
    opt_in_site_apps: false, // Disable site apps to prevent the specific error
    autocapture: false, // Disable autocapture to reduce noise
    loaded: (posthog) => {
      if (process.env.NODE_ENV === "development") {
        console.log("PostHog loaded successfully");
      }
    },
  });
} catch (error) {
  if (process.env.NODE_ENV === "development") {
    console.error("PostHog initialization error:", error);
  }
}

// Add a check to ensure analytics is properly initialized
if (!analyticsInstance) {
  if (process.env.NODE_ENV === "development") {
    console.warn("PostHog failed to initialize - creating a stub");
  }
  // Create a stub that won't throw errors
  analyticsInstance = {
    capture: () => {},
    identify: () => {},
    reset: () => {},
    // Add other methods as needed
  } as any;
}

export const analytics = analyticsInstance as PostHog;

export const PostHogProvider = ({ children }: { children: ReactNode }) => (
  <PostHogProviderRaw client={analytics}>{children}</PostHogProviderRaw>
);
